import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  CreatorBrief,
  CreatorBriefsState,
} from "../types/creatorBriefsTypes.tsx";
import {
  fetchCreatorBriefsThunk,
  fetchCreatorBriefByIdThunk,
  createCreatorBriefThunk,
  updateCreatorBriefThunk,
  deleteCreatorBriefThunk,
  downloadCreatorBrief,
} from "../thunks/creatorBriefsThunk.tsx";

const initialState: CreatorBriefsState = {
  isLoading: false,
  isDeleting: false,
  isDownloading: false,
  isPolling: false,
  briefs: null,
  selectedBrief: null,
  error: null,
};

export const creatorBriefsSlice = createSlice({
  name: "creatorBriefs",
  initialState,
  reducers: {
    resetSelectedBrief: (state) => {
      state.selectedBrief = null;
      state.error = null;
      state.isLoading = false;
    },
    clearBriefs: (state) => {
      state.briefs = initialState.briefs;
    },
    clearBriefById: (state) => {
      state.selectedBrief = initialState.selectedBrief;
    },
    setIsPolling: (state, action: PayloadAction<boolean>) => {
      state.isPolling = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCreatorBriefsThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(
      fetchCreatorBriefsThunk.fulfilled,
      (state, action: PayloadAction<CreatorBrief[]>) => {
        state.isLoading = false;
        state.briefs = action.payload;
      },
    );
    builder.addCase(fetchCreatorBriefsThunk.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    builder.addCase(fetchCreatorBriefByIdThunk.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      fetchCreatorBriefByIdThunk.fulfilled,
      (state, action: PayloadAction<CreatorBrief>) => {
        state.isLoading = false;
        state.selectedBrief = action.payload;
      },
    );
    builder.addCase(fetchCreatorBriefByIdThunk.rejected, (state, action) => {
      state.isLoading = false;
      state.error = (action.payload as string) || "Failed to fetch brief.";
    });

    builder.addCase(
      createCreatorBriefThunk.fulfilled,
      (state, action: PayloadAction<CreatorBrief>) => {
        state.briefs = [action.payload, ...(state.briefs || [])];
      },
    );

    builder.addCase(
      updateCreatorBriefThunk.fulfilled,
      (
        state,
        action: PayloadAction<
          Partial<CreatorBrief>,
          string,
          { arg: { id: string | number } }
        >,
      ) => {
        const updatedData = action.payload;
        const briefId = action.meta.arg.id;

        if (state.briefs && briefId) {
          const index = state.briefs.findIndex((b) => b.id === briefId);
          if (index !== -1) {
            state.briefs[index] = { ...state.briefs[index], ...updatedData };
          }
        }
        if (state.selectedBrief && state.selectedBrief.id === briefId) {
          state.selectedBrief = { ...state.selectedBrief, ...updatedData };
        }
      },
    );

    builder.addCase(
      deleteCreatorBriefThunk.fulfilled,
      (state, action: PayloadAction<string | number>) => {
        if (state.briefs) {
          state.briefs = state.briefs.filter((b) => b.id !== action.payload);
        }
      },
    );
    builder.addCase(downloadCreatorBrief.pending, (state) => {
      state.isDownloading = true;
    });
    builder.addCase(downloadCreatorBrief.fulfilled, (state) => {
      state.isDownloading = false;
    });
    builder.addCase(downloadCreatorBrief.rejected, (state) => {
      state.isDownloading = false;
    });
  },
});

export const { resetSelectedBrief, clearBriefs, clearBriefById, setIsPolling } =
  creatorBriefsSlice.actions;
export default creatorBriefsSlice.reducer;
